/**
 * CosmeticCatalog — meets or exceeds the spec §12.3 free-catalog minimums and
 * the §13.1 Prism Timeline Pack contents. Cosmetics NEVER alter geometry,
 * hitboxes, speed, score, visibility rules, or leaderboard eligibility.
 *
 * Readability rules (spec §12.2) are encoded in the values themselves:
 * player cores stay the brightest cyan-adjacent object, shards keep gold/white
 * high-luminance centers, every echo keeps a unique silhouette + notch count.
 */

export type SlotId =
  | "core"
  | "trail"
  | "palette"
  | "shapes"
  | "shard"
  | "arena"
  | "instrument"
  | "frame";

export interface CatalogItem {
  id: string;
  name: string;
  /** default-owned */
  free?: boolean;
  /** unlocked by achievement id */
  ach?: string;
  /** part of the Prism Timeline Pack */
  prism?: boolean;
  /** swatch colors for UI */
  cols: string[];
  /** slot-specific payload */
  data?: Record<string, unknown>;
}

export interface CoreStyle {
  ring: string;
  core: string;
  halo?: boolean;
  orbit?: boolean;
}

export const ECHO_SHAPE_SETS: Record<string, ["circle" | "ring", string, string, string]> = {};

export const CATALOG: Record<SlotId, CatalogItem[]> = {
  core: [
    { id: "pulse", name: "PULSE", free: true, cols: ["#B9F8FF", "#42DAF6"], data: { ring: "#42DAF6", core: "#B9F8FF" } },
    { id: "ember", name: "EMBER", free: true, cols: ["#EFFFF6", "#4FE3D0"], data: { ring: "#4FE3D0", core: "#EFFFF6" } },
    { id: "drift", name: "DRIFT", free: true, cols: ["#E8F4FF", "#5FC8FF"], data: { ring: "#5FC8FF", core: "#E8F4FF" } },
    { id: "halo", name: "HALO", ach: "timeline_clear", cols: ["#FFFFFF", "#7DF9E4"], data: { ring: "#7DF9E4", core: "#FFFFFF", halo: true } },
    { id: "ion", name: "ION", prism: true, cols: ["#E7FCFF", "#66E6FF"], data: { ring: "#66E6FF", core: "#E7FCFF", orbit: true } },
    { id: "nova", name: "NOVA", prism: true, cols: ["#F4FBFF", "#8FD8FF"], data: { ring: "#8FD8FF", core: "#F4FBFF", halo: true } },
    { id: "prismcore", name: "PRISM", prism: true, cols: ["#FFFFFF", "#9BE8FF"], data: { ring: "#9BE8FF", core: "#FFFFFF", orbit: true } },
    { id: "eclipse", name: "ECLIPSE", prism: true, cols: ["#DFFBFF", "#3FD2FF"], data: { ring: "#3FD2FF", core: "#DFFBFF", halo: true } },
  ],
  trail: [
    { id: "standard", name: "STANDARD", free: true, cols: ["#42DAF6"], data: { col: "rgba(66,218,246,0.20)", width: 1.6 } },
    { id: "comet", name: "COMET", free: true, cols: ["#8FE9FF"], data: { col: "rgba(143,233,255,0.24)", width: 2.0, taper: true } },
    { id: "glass", name: "GLASS", ach: "untouched", cols: ["#DDEEFF"], data: { col: "rgba(221,238,255,0.28)", width: 1.6 } },
    { id: "edge", name: "EDGE PULSE", ach: "boundary_dancer", cols: ["#7DF9E4"], data: { col: "rgba(125,249,228,0.30)", width: 1.6, pulse: true } },
    { id: "prismline", name: "PRISM LINE", prism: true, cols: ["#FF61D2", "#42DAF6"], data: { col: "rgba(255,97,210,0.28)", width: 1.8, duo: "rgba(66,218,246,0.28)" } },
    { id: "ribbon", name: "RIBBON", prism: true, cols: ["#C9B8FF"], data: { col: "rgba(201,184,255,0.30)", width: 2.4, taper: true } },
    { id: "stardust", name: "STARDUST", prism: true, cols: ["#FFF3C4"], data: { col: "rgba(255,243,196,0.26)", width: 1.4, dotted: true } },
    { id: "ionwake", name: "IONWAKE", prism: true, cols: ["#66E6FF"], data: { col: "rgba(102,230,255,0.30)", width: 2.0, taper: true } },
  ],
  palette: [
    { id: "standard", name: "STANDARD", free: true, cols: ["#FF61D2", "#9F79FF", "#FF5478", "#6B8DFF"] },
    { id: "twilight", name: "TWILIGHT", free: true, cols: ["#FF7BE2", "#8E7BFF", "#FF6E92", "#5FA8FF"] },
    { id: "emberline", name: "EMBERLINE", free: true, cols: ["#FF8A5C", "#C86BFF", "#FF5478", "#7B9DFF"] },
    { id: "magenta", name: "MAGENTA DRIFT", ach: "first_echo", cols: ["#FF61D2", "#FF7BE2", "#E14FB8", "#B84FFF"] },
    { id: "solar", name: "SOLAR FLARE", prism: true, cols: ["#FFB65C", "#FF8A5C", "#FF61A8", "#FFD75C"] },
    { id: "ultraviolet", name: "ULTRAVIOLET", prism: true, cols: ["#C86BFF", "#9F79FF", "#7B5CFF", "#E09BFF"] },
    { id: "glacier", name: "GLACIER", prism: true, cols: ["#7FA8FF", "#6B8DFF", "#9F79FF", "#5C8EE8"] },
    { id: "rosefield", name: "ROSEFIELD", prism: true, cols: ["#FF9BC0", "#FF61D2", "#D6548F", "#FF7BAA"] },
    { id: "ionstorm", name: "IONSTORM", prism: true, cols: ["#6FA0FF", "#8A7BFF", "#B08AFF", "#4F8AE3"] },
    { id: "palefire", name: "PALE FIRE", prism: true, cols: ["#FFE0B8", "#FFB65C", "#FF9BC0", "#E8D5FF"] },
  ],
  shapes: [
    { id: "classic", name: "CLASSIC", free: true, cols: ["#F2F5FF"], data: { set: ["circle", "diamond", "split", "hex"] } },
    { id: "orbitals", name: "ORBITALS", free: true, cols: ["#DDE2F5"], data: { set: ["ringdot", "triangle", "square", "octagon"] } },
  ],
  shard: [
    { id: "star4", name: "FOUR POINT", free: true, cols: ["#FFE37B"], data: { points: 4 } },
    { id: "amber", name: "AMBER", ach: "first_light", cols: ["#FFC96B"], data: { points: 4, fill: "#FFC96B" } },
    { id: "octa", name: "EIGHT POINT", ach: "combo_four", cols: ["#FFE37B", "#FFF3C4"], data: { points: 8 } },
    { id: "prismshard", name: "PRISM SHARD", prism: true, cols: ["#FFE37B", "#FF61D2"], data: { points: 4, fill: "#FFD0EE" } },
  ],
  arena: [
    { id: "observatory", name: "OBSERVATORY", free: true, cols: ["#0A1022"], data: { top: "#071424", mid: "#0A1022", bot: "#130A20", grid: "rgba(66,218,246,0.055)" } },
    { id: "deepfield", name: "DEEP FIELD", free: true, cols: ["#060B18"], data: { top: "#04070F", mid: "#060B18", bot: "#0B0A1E", grid: "rgba(107,141,255,0.06)", stars: true } },
    { id: "violet", name: "VIOLET GRID", ach: "score_1000", cols: ["#1B1038"], data: { top: "#100A2A", mid: "#140E30", bot: "#1B1038", grid: "rgba(159,121,255,0.10)" } },
    { id: "aurora", name: "AURORA", prism: true, cols: ["#08202A", "#130A20"], data: { top: "#062028", mid: "#0A1022", bot: "#160A24", grid: "rgba(79,227,208,0.06)" } },
    { id: "redshift", name: "REDSHIFT", prism: true, cols: ["#200A18"], data: { top: "#1A0714", mid: "#180A1E", bot: "#0E0A24", grid: "rgba(255,97,210,0.07)" } },
    { id: "voidline", name: "VOIDLINE", prism: true, cols: ["#05070D"], data: { top: "#04060B", mid: "#05070D", bot: "#090714", grid: "rgba(167,171,192,0.08)" } },
  ],
  instrument: [
    { id: "puresine", name: "PURE SINE", free: true, cols: ["#B9F8FF"], data: { wave: "sine" } },
    { id: "softpluck", name: "SOFT PLUCK", free: true, cols: ["#DDE2F5"], data: { wave: "triangle" } },
    { id: "octave", name: "OCTAVE", ach: "combo_eight", cols: ["#FFE37B"], data: { wave: "sine", octaveDouble: true } },
    { id: "glassbells", name: "GLASS BELLS", prism: true, cols: ["#E7FCFF"], data: { wave: "sine", bell: true } },
    { id: "deeppad", name: "DEEP PAD", prism: true, cols: ["#9F79FF"], data: { wave: "sawtooth", soft: true } },
  ],
  frame: [
    { id: "plain", name: "PLAIN", free: true, cols: ["#0A1022"] },
    { id: "meridian", name: "MERIDIAN", free: true, cols: ["#42DAF6"] },
    { id: "aurora_f", name: "AURORA FRAME", ach: "score_2000", cols: ["#42DAF6", "#9F79FF"] },
    { id: "archive", name: "ARCHIVE", ach: "archivist", cols: ["#A7ABC0"] },
    { id: "orbital", name: "ORBITAL", prism: true, cols: ["#FFE37B"] },
    { id: "spectrum", name: "SPECTRUM", prism: true, cols: ["#FF61D2", "#42DAF6"] },
    { id: "gilded", name: "GILDED", prism: true, cols: ["#FFE37B", "#FFF3C4"] },
    { id: "nebula", name: "NEBULA", prism: true, cols: ["#9F79FF", "#FF61D2"] },
  ],
};

export const ACHIEVEMENTS: Record<string, { name: string; desc: string }> = {
  first_light: { name: "FIRST LIGHT", desc: "Collect your first shard" },
  first_echo: { name: "FIRST ECHO", desc: "Survive Echo A for 5 seconds" },
  timeline_clear: { name: "TIMELINE CLEAR", desc: "Complete a 30-second run" },
  untouched: { name: "UNTOUCHED", desc: "Clear with all 3 lives" },
  combo_four: { name: "COMBO FOUR", desc: "Reach combo x4" },
  combo_eight: { name: "COMBO EIGHT", desc: "Reach combo x8" },
  score_1000: { name: "KILOLIGHT", desc: "Score 1,000 in one run" },
  score_2000: { name: "AURORA CLASS", desc: "Score 2,000 in one run" },
  boundary_dancer: { name: "BOUNDARY DANCER", desc: "Dance the edge for 10s and clear" },
  archivist: { name: "ARCHIVIST", desc: "Save 10 Traceprints" },
};

/** Achievement id → the catalog item it unlocks. */
export function achievementReward(achId: string): { slot: SlotId; item: CatalogItem } | null {
  for (const slot of Object.keys(CATALOG) as SlotId[]) {
    const item = CATALOG[slot].find((i) => i.ach === achId);
    if (item) return { slot, item };
  }
  return null;
}

/**
 * Timeline Rating — informational rank tiers derived from the unassisted
 * Classic best. Purely presentational: no gameplay or matchmaking effect.
 */
export const RANK_TIERS: { name: string; min: number; col: string }[] = [
  { name: "DRIFTER", min: 0, col: "#A7ABC0" },
  { name: "TRACER", min: 400, col: "#42DAF6" },
  { name: "WEAVER", min: 900, col: "#4FE3D0" },
  { name: "CHRONONAUT", min: 1500, col: "#9F79FF" },
  { name: "PARADOX", min: 2400, col: "#FF61D2" },
  { name: "EIGENSTATE", min: 3500, col: "#FFE37B" },
];

export function rankFor(best: number): { name: string; col: string; next: number | null; prev: number } {
  let tier = RANK_TIERS[0];
  let next: number | null = null;
  for (let i = 0; i < RANK_TIERS.length; i++) {
    if (best >= RANK_TIERS[i].min) {
      tier = RANK_TIERS[i];
      next = i + 1 < RANK_TIERS.length ? RANK_TIERS[i + 1].min : null;
    }
  }
  return { name: tier.name, col: tier.col, next, prev: tier.min };
}
